/**
 * Calm-minute breathing session.
 *
 * Pure timing: patterns, phase sequence, whole-second countdown, and stop.
 * The page paints the ball; this module does not touch the network, Firebase,
 * or any storage key except the optional last-chosen pattern.
 */

export const CALM_MINUTE_MS = 60_000;
export const SCALE_MIN = 0.58;
export const SCALE_MAX = 1;
export const PATTERN_STORAGE_KEY = 'focus-log-breathe-pattern';
export const DEFAULT_PATTERN_ID = '4-4-6';

export type PhaseKind = 'in' | 'hold' | 'out';
export type PhaseLabel = 'Breathe in' | 'Hold' | 'Breathe out';
export type PatternId = '4-4-6' | 'box' | '4-2-6';
export type SessionStatus = 'idle' | 'running' | 'ended';

export interface PhaseStep {
  kind: PhaseKind;
  seconds: number;
  label: PhaseLabel;
}

export interface Pattern {
  id: PatternId;
  name: string;
  /** Plain description of the counts. Uses "and", never "&". */
  detail: string;
  phases: PhaseStep[];
}

export interface BreathSession {
  status: SessionStatus;
  patternId: PatternId;
  elapsedMs: number;
}

export interface BreathFrame {
  timeLeftSeconds: number;
  phaseLabel: PhaseLabel;
  phaseKind: PhaseKind;
  phaseSecondsLeft: number;
  phaseProgress: number;
  /** Smooth size while motion is allowed. */
  scale: number;
  opacity: number;
  /** One size per phase when the user prefers reduced motion. */
  reducedScale: number;
  reducedOpacity: number;
}

export const BREATHE_COPY = {
  product: 'Focus Log',
  title: 'A calm minute',
  lede: 'Follow the ball for one minute.',
  noSignIn: 'No sign-in.',
  end: 'That was a calm minute.',
  start: 'Start',
  stop: 'Stop',
  again: 'Go again',
  tryApp: 'Try Focus Log',
  brand: 'Team GOACTO',
  patternLegend: 'Pattern',
} as const;

export const PATTERNS: readonly Pattern[] = [
  {
    id: '4-4-6',
    name: '4-4-6',
    detail: 'In 4, hold 4, and out 6',
    phases: [
      { kind: 'in', seconds: 4, label: 'Breathe in' },
      { kind: 'hold', seconds: 4, label: 'Hold' },
      { kind: 'out', seconds: 6, label: 'Breathe out' },
    ],
  },
  {
    id: 'box',
    name: 'Box',
    detail: 'In 4, hold 4, out 4, and hold 4',
    phases: [
      { kind: 'in', seconds: 4, label: 'Breathe in' },
      { kind: 'hold', seconds: 4, label: 'Hold' },
      { kind: 'out', seconds: 4, label: 'Breathe out' },
      { kind: 'hold', seconds: 4, label: 'Hold' },
    ],
  },
  {
    id: '4-2-6',
    name: '4-2-6',
    detail: 'In 4, hold 2, and out 6',
    phases: [
      { kind: 'in', seconds: 4, label: 'Breathe in' },
      { kind: 'hold', seconds: 2, label: 'Hold' },
      { kind: 'out', seconds: 6, label: 'Breathe out' },
    ],
  },
];

const PATTERN_IDS = new Set<string>(PATTERNS.map((pattern) => pattern.id));

export function isPatternId(value: string | null | undefined): value is PatternId {
  return !!value && PATTERN_IDS.has(value);
}

export function isBreathePath(pathname: string): boolean {
  if (typeof pathname !== 'string' || pathname.length === 0) return false;
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return path === '/breathe';
}

export function getPattern(id: PatternId): Pattern {
  return PATTERNS.find((pattern) => pattern.id === id) ?? PATTERNS[0];
}

export function createSession(patternId: PatternId = DEFAULT_PATTERN_ID): BreathSession {
  return { status: 'idle', patternId, elapsedMs: 0 };
}

export function selectPattern(session: BreathSession, patternId: PatternId): BreathSession {
  if (session.status === 'running') return session;
  return { ...session, patternId };
}

export function startSession(session: BreathSession): BreathSession {
  if (session.status === 'running') return session;
  return { status: 'running', patternId: session.patternId, elapsedMs: 0 };
}

/** Stop ends the minute and clears the clock so the next start is fresh. */
export function stopSession(session: BreathSession): BreathSession {
  if (session.status !== 'running') return session;
  return { status: 'ended', patternId: session.patternId, elapsedMs: 0 };
}

export function applyElapsed(session: BreathSession, elapsedMs: number): BreathSession {
  if (session.status !== 'running') return session;
  const elapsed = Math.max(0, elapsedMs);
  if (elapsed >= CALM_MINUTE_MS) {
    return { ...session, status: 'ended', elapsedMs: CALM_MINUTE_MS };
  }
  return { ...session, elapsedMs: elapsed };
}

export function readStoredPattern(storage: Pick<Storage, 'getItem'> | undefined): PatternId {
  try {
    const value = storage?.getItem(PATTERN_STORAGE_KEY);
    if (isPatternId(value)) return value;
  } catch {
    // Ignore blocked storage. The default pattern still works.
  }
  return DEFAULT_PATTERN_ID;
}

export function writeStoredPattern(storage: Pick<Storage, 'setItem'> | undefined, id: PatternId): void {
  try {
    storage?.setItem(PATTERN_STORAGE_KEY, id);
  } catch {
    // Private mode can reject writes. The choice still lasts for this visit.
  }
}

/**
 * Read the app's saved theme without loading the session store.
 * Defaults to dark, matching Focus Log.
 */
export function readFocusLogDark(storage: Pick<Storage, 'getItem'> | undefined): boolean {
  try {
    const raw = storage?.getItem('goacto-storage');
    if (!raw) return true;
    const parsed = JSON.parse(raw) as { state?: { isDarkMode?: unknown } };
    if (typeof parsed.state?.isDarkMode === 'boolean') return parsed.state.isDarkMode;
  } catch {
    // A bad saved blob should not change this page.
  }
  return true;
}

function clamp01(value: number): number {
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

function easeInOut(value: number): number {
  return 0.5 - 0.5 * Math.cos(Math.PI * clamp01(value));
}

function lerp(from: number, to: number, amount: number): number {
  return from + (to - from) * amount;
}

interface PhasePosition {
  step: PhaseStep;
  index: number;
  progress: number;
}

function phaseAt(phases: readonly PhaseStep[], elapsedMs: number): PhasePosition {
  const cycleMs = phases.reduce((sum, step) => sum + step.seconds * 1000, 0);
  const into = cycleMs > 0 ? elapsedMs % cycleMs : 0;
  let cursor = 0;
  for (let index = 0; index < phases.length; index += 1) {
    const step = phases[index];
    const duration = step.seconds * 1000;
    const isLast = index === phases.length - 1;
    if (into < cursor + duration || isLast) {
      const phaseElapsed = Math.max(0, into - cursor);
      return {
        step,
        index,
        progress: duration > 0 ? clamp01(phaseElapsed / duration) : 0,
      };
    }
    cursor += duration;
  }
  return { step: phases[0], index: 0, progress: 0 };
}

function holdIsFull(phases: readonly PhaseStep[], index: number): boolean {
  return index > 0 && phases[index - 1].kind === 'in';
}

function smoothScale(phases: readonly PhaseStep[], position: PhasePosition): number {
  const eased = easeInOut(position.progress);
  if (position.step.kind === 'in') return lerp(SCALE_MIN, SCALE_MAX, eased);
  if (position.step.kind === 'out') return lerp(SCALE_MAX, SCALE_MIN, eased);
  return holdIsFull(phases, position.index) ? SCALE_MAX : SCALE_MIN;
}

function steppedScale(phases: readonly PhaseStep[], position: PhasePosition): number {
  if (position.step.kind === 'in') return 0.84;
  if (position.step.kind === 'out') return 0.66;
  return holdIsFull(phases, position.index) ? 0.94 : SCALE_MIN;
}

function smoothOpacity(step: PhaseStep, progress: number): number {
  const eased = easeInOut(progress);
  if (step.kind === 'in') return lerp(0.9, 1, eased);
  if (step.kind === 'out') return lerp(1, 0.85, eased);
  return 1;
}

function steppedOpacity(phases: readonly PhaseStep[], position: PhasePosition): number {
  if (position.step.kind === 'out') return 0.72;
  if (position.step.kind === 'hold' && !holdIsFull(phases, position.index)) return 0.78;
  return 1;
}

function timeLeftSeconds(elapsedMs: number): number {
  if (elapsedMs >= CALM_MINUTE_MS) return 0;
  return Math.ceil((CALM_MINUTE_MS - elapsedMs) / 1000);
}

function endedFrame(): BreathFrame {
  return {
    timeLeftSeconds: 0,
    phaseLabel: 'Breathe out',
    phaseKind: 'out',
    phaseSecondsLeft: 0,
    phaseProgress: 1,
    scale: SCALE_MIN,
    opacity: 0.95,
    reducedScale: SCALE_MIN,
    reducedOpacity: 0.95,
  };
}

export function project(session: BreathSession): BreathFrame {
  if (session.status === 'ended' || session.elapsedMs >= CALM_MINUTE_MS) return endedFrame();
  const pattern = getPattern(session.patternId);
  const elapsed = session.status === 'running' ? session.elapsedMs : 0;
  const position = phaseAt(pattern.phases, elapsed);
  const durationMs = position.step.seconds * 1000;
  const remainingMs = durationMs * (1 - position.progress);
  return {
    timeLeftSeconds: timeLeftSeconds(elapsed),
    phaseLabel: position.step.label,
    phaseKind: position.step.kind,
    phaseSecondsLeft: Math.max(1, Math.ceil(remainingMs / 1000)),
    phaseProgress: position.progress,
    scale: smoothScale(pattern.phases, position),
    opacity: smoothOpacity(position.step, position.progress),
    reducedScale: steppedScale(pattern.phases, position),
    reducedOpacity: steppedOpacity(pattern.phases, position),
  };
}
